import React from 'react';
import { Link } from 'react-router-dom';
import {
	Row, Col, Card, Form, Input, Button, Select, DatePicker,
	InputNumber, Typography, Alert,
} from 'antd';
import {
	IdcardOutlined, CarOutlined, EnvironmentOutlined, SafetyOutlined,
	CalendarOutlined, SaveOutlined, ArrowLeftOutlined, SearchOutlined,
	PlusCircleOutlined,
} from '@ant-design/icons';
import { connect } from 'react-redux';
import { initHandler, ajaxViewHandler, ajaxHandler, extractResult } from '../../store/actions';
import { Helmet } from 'react-helmet';
import Config from '../../Config';
import { toastSuccess, toastError } from '../../utils/toast';
import ErrorAlertModal from '../../components/ErrorAlertModal';

const { Text } = Typography;
const { TextArea } = Input;

const configClass = {
	title: 'Tambah Kendaraan Manual',
	apiUrl: {
		tambah: 'master/api/kendaraan-pencarian/tambah-manual/',
		pemilik: 'pemilik/api/wajib/pajak/',
		jenisKendaraan: 'master/api/jenis/kendaraan/',
		merek: 'master/api/merek/',
		tipe: 'master/api/tipe/',
		bahanBakar: 'master/api/bahan/bakar/',
		warnaPlat: 'master/api/plat/',
		fungsi: 'master/api/fungsi/',
		golongan: 'master/api/golongan/',
		kepemilikan: 'master/api/kepemilikan/',
		kabupaten: 'master/api/kabupaten/',
		kecamatan: 'master/api/kecamatan/',
		desa: 'master/api/desa/',
	},
	pageUrl: { data: '/admin/kendaraan' },
};

const config = new Config();

const DATE_FIELDS = [
	{ name: 'stnk_mulai', label: 'STNK Mulai' },
	{ name: 'stnk_akhir', label: 'STNK Akhir' },
	{ name: 'sd_swdkllj', label: 'Masa Berlaku Akhir SWDKLLJ' },
	{ name: 'tanggal_notice', label: 'Notice - Pajak Mulai' },
	{ name: 'sd_notice', label: 'Notice - Pajak Akhir' },
];

const sectionCard = (title, icon, color, children) => (
	<Card style={{ borderRadius: 14, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', overflow: 'hidden', marginBottom: 20 }} bodyStyle={{ padding: 0 }}>
		<div style={{ padding: '14px 24px', background: `linear-gradient(135deg, ${color}22, ${color}11)`, borderBottom: `2px solid ${color}33`, display: 'flex', alignItems: 'center', gap: 10 }}>
			<span style={{ color, fontSize: 18 }}>{icon}</span>
			<Text strong style={{ fontSize: 15, color: '#222' }}>{title}</Text>
		</div>
		<div style={{ padding: '24px 28px' }}>{children}</div>
	</Card>
);

/**
 * Form "Tambah Kendaraan Manual" - antisipasi kendaraan lama yang belum
 * pernah tercatat sama sekali di sistem (baru maupun legacy). Akses
 * digerbang hak_akses `master_kendaraan_tambah_manual` di routing
 * AdminLayout.jsx + ditegakkan ulang di backend (lihat diskusi 2026-10-13).
 *
 * TIDAK membuat Tagihan sama sekali (is_migrate=True di backend) - field
 * tanggal pajak di sini OPSIONAL, diisi staf berdasarkan dokumen fisik
 * (STNK/notice lama) kalau memang ada.
 */
class FormTambahKendaraan extends React.Component {
	formRef = React.createRef();

	constructor(props) {
		super(props);
		this.state = {
			errTitle: '', errMsg: '',
			loadingSubmit: false,
			loadingCekNik: false,
			pemilikDitemukan: null, // {nama, nik, alamat, no_hp} kalau NIK sudah terdaftar
			nikSudahDicek: false,
			listJenisKendaraan: [], listMerek: [], listTipe: [],
			listBahanBakar: [], listWarnaPlat: [], listFungsi: [],
			listGolongan: [], listKepemilikan: [],
			listKabupaten: [], listKecamatan: [], listDesa: [],
		};
	}

	UNSAFE_componentWillMount() { this.props.initHandler(); }

	componentDidMount() { this._loadIndependentLists(); }

	_fetchList = (url, stateKey, extraParams = '') => {
		this.props.ajaxViewHandler('get', `${url}?limit=500${extraParams}`)
			.then((action) => {
				const { isSuccess, responseMsg: data } = extractResult(action);
				if (isSuccess) this.setState({ [stateKey]: (data && data.results) || [] });
			});
	};

	_loadIndependentLists = () => {
		this._fetchList(configClass.apiUrl.jenisKendaraan, 'listJenisKendaraan');
		this._fetchList(configClass.apiUrl.bahanBakar, 'listBahanBakar');
		this._fetchList(configClass.apiUrl.warnaPlat, 'listWarnaPlat');
		this._fetchList(configClass.apiUrl.fungsi, 'listFungsi');
		this._fetchList(configClass.apiUrl.golongan, 'listGolongan');
		this._fetchList(configClass.apiUrl.kepemilikan, 'listKepemilikan');
		this._fetchList(configClass.apiUrl.kabupaten, 'listKabupaten');
	};

	// ── Cascading: Jenis Kendaraan -> Merek -> Tipe ──────────────────────
	handleJenisChange = (val) => {
		this.formRef.current.setFieldsValue({ merek: undefined, tipe: undefined });
		this.setState({ listMerek: [], listTipe: [] });
		if (val) this._fetchList(configClass.apiUrl.merek, 'listMerek', `&jenis=${val}`);
	};

	handleMerekChange = (val) => {
		this.formRef.current.setFieldsValue({ tipe: undefined });
		this.setState({ listTipe: [] });
		const jenis = this.formRef.current.getFieldValue('jenis_kendaraan');
		const tahun = this.formRef.current.getFieldValue('tahun_buat');
		if (val) this._fetchList(configClass.apiUrl.tipe, 'listTipe', `&merek=${val}&jenis=${jenis || ''}&tahun=${tahun || ''}`);
	};

	// ── Cascading: Kabupaten -> Kecamatan -> Desa ────────────────────────
	handleKabupatenChange = (val) => {
		this.formRef.current.setFieldsValue({ kecamatan: undefined, desa: undefined });
		this.setState({ listKecamatan: [], listDesa: [] });
		if (val) this._fetchList(configClass.apiUrl.kecamatan, 'listKecamatan', `&regencies=${val}`);
	};

	handleKecamatanChange = (val) => {
		this.formRef.current.setFieldsValue({ desa: undefined });
		this.setState({ listDesa: [] });
		if (val) this._fetchList(configClass.apiUrl.desa, 'listDesa', `&district=${val}`);
	};

	// ── Cek NIK - supaya staf tahu kalau NIK sudah terdaftar SEBELUM submit
	// (lihat diskusi 2026-10-13 - pemilik existing TIDAK PERNAH ditimpa
	// lewat form ini, nama/alamat yang diketik di sini cuma jadi snapshot
	// khusus kendaraan ini). ──────────────────────────────────────────────
	handleCekNik = () => {
		const nik = (this.formRef.current.getFieldValue('pemilik_nik') || '').trim();
		if (!nik) return;
		this.setState({ loadingCekNik: true, nikSudahDicek: false });
		this.props.ajaxViewHandler('get', `${configClass.apiUrl.pemilik}?nik=${encodeURIComponent(nik)}`)
			.then((action) => {
				const { isSuccess, responseMsg: data } = extractResult(action);
				const results = (data && data.results) || (Array.isArray(data) ? data : []);
				this.setState({
					loadingCekNik: false,
					nikSudahDicek: true,
					pemilikDitemukan: (isSuccess && results && results.length > 0) ? results[0] : null,
				});
			})
			.catch(() => this.setState({ loadingCekNik: false, nikSudahDicek: true, pemilikDitemukan: null }));
	};

	handleSubmit = (values) => {
		const payload = { ...values };
		DATE_FIELDS.forEach(({ name }) => {
			// Backend DRF DATE_INPUT_FORMATS cuma terima %d-%m-%Y.
			payload[name] = values[name] ? values[name].format('DD-MM-YYYY') : null;
		});
		this.setState({ loadingSubmit: true });
		this.props.ajaxHandler('post', configClass.apiUrl.tambah, payload, false, true)
			.then(() => {
				this.setState({ loadingSubmit: false });
				if (this.props.isSuccess) {
					toastSuccess('Kendaraan berhasil ditambahkan');
					this.props.history.push(configClass.pageUrl.data);
				} else {
					this.setState({ errTitle: 'Gagal menambah kendaraan', errMsg: this.props.responseMsg });
					toastError('Gagal menambah kendaraan');
				}
			})
			.catch(() => { this.setState({ loadingSubmit: false }); toastError('Kesalahan jaringan, coba lagi'); });
	};

	_renderSelect(field, list, placeholder, onChange, labelField = 'nama', required = false) {
		return (
			<Form.Item label={placeholder} name={field} rules={required ? [{ required: true, message: `${placeholder} wajib dipilih` }] : []}>
				<Select allowClear showSearch optionFilterProp="children" placeholder={`Pilih ${placeholder.toLowerCase()}...`}
					style={{ borderRadius: 8 }} onChange={onChange}>
					{list.map(item => <Select.Option key={item.id} value={item.id}>{item[labelField]}</Select.Option>)}
				</Select>
			</Form.Item>
		);
	}

	render() {
		const { errTitle, errMsg, loadingSubmit, loadingCekNik, pemilikDitemukan, nikSudahDicek,
			listJenisKendaraan, listMerek, listTipe, listBahanBakar, listWarnaPlat,
			listFungsi, listGolongan, listKepemilikan,
			listKabupaten, listKecamatan, listDesa } = this.state;

		return (
			<div>
				<Helmet><title>{configClass.title + ' | ' + config.get_site_info().name}</title></Helmet>
				<ErrorAlertModal visible={errMsg !== ''} title={errTitle} error={errMsg} onClose={() => this.setState({ errTitle: '', errMsg: '' })} />

				<Card style={{ borderRadius: 14, boxShadow: '0 4px 20px rgba(0,0,0,0.07)', overflow: 'hidden', marginBottom: 20 }} bodyStyle={{ padding: 0 }}>
					<div style={{ padding: '16px 20px', background: 'linear-gradient(135deg, #0f2847, #1e4d8a)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
						<div style={{ color: '#fff', fontWeight: 700, fontSize: 17 }}><PlusCircleOutlined style={{ marginRight: 8 }} />{configClass.title}</div>
						<Link to={configClass.pageUrl.data}>
							<Button icon={<ArrowLeftOutlined />} style={{ borderRadius: 8, background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff' }}>Kembali</Button>
						</Link>
					</div>
				</Card>

				<Alert
					type="info" showIcon style={{ borderRadius: 8, marginBottom: 20 }}
					message="Khusus kendaraan lama yang belum tercatat sama sekali"
					description="Field Tanggal Pajak di bawah OPSIONAL - isi berdasarkan STNK/notice fisik yang ada. Mengosongkannya TIDAK membuat transaksi/tagihan apa pun - kendaraan tetap tercatat, tagihan diisi nanti lewat menu Pendaftaran biasa."
				/>

				<Form ref={this.formRef} layout="vertical" onFinish={this.handleSubmit}>
					{sectionCard('Data Pemilik', <IdcardOutlined />, '#fa8c16', (
						<>
							<Row gutter={16}>
								<Col xs={24} sm={12}>
									<Form.Item label="NIK" name="pemilik_nik" rules={[{ required: true, message: 'NIK wajib diisi' }]}>
										<Input style={{ borderRadius: 8 }} onBlur={this.handleCekNik}
											suffix={<Button type="link" size="small" icon={<SearchOutlined />} loading={loadingCekNik} onClick={this.handleCekNik} style={{ padding: 0 }}>Cek</Button>} />
									</Form.Item>
								</Col>
								<Col xs={24} sm={12}><Form.Item label="Nama Pemilik" name="pemilik_nama" rules={[{ required: true, message: 'Nama wajib diisi' }]}><Input style={{ borderRadius: 8 }} /></Form.Item></Col>
								<Col xs={24} sm={12}><Form.Item label="Alamat" name="pemilik_alamat"><TextArea rows={2} style={{ borderRadius: 8 }} /></Form.Item></Col>
								<Col xs={24} sm={12}><Form.Item label="No HP" name="pemilik_no_hp"><Input style={{ borderRadius: 8 }} /></Form.Item></Col>
							</Row>
							{nikSudahDicek && pemilikDitemukan && (
								<Alert
									type="info" showIcon style={{ borderRadius: 8, marginTop: 4 }}
									message="NIK ini sudah terdaftar di sistem"
									description={`Atas nama: ${pemilikDitemukan.nama || '-'}, alamat: ${pemilikDitemukan.alamat || '-'}. Data pemilik yang SUDAH ADA ini TIDAK akan ditimpa - nama/alamat yang kamu isi di atas hanya dipakai sebagai catatan khusus kendaraan ini (mis. kalau pemilik sudah pindah alamat untuk kendaraan ini).`}
								/>
							)}
							{nikSudahDicek && !pemilikDitemukan && (
								<Alert
									type="success" showIcon style={{ borderRadius: 8, marginTop: 4 }}
									message="NIK belum terdaftar - akan dibuat data pemilik baru"
								/>
							)}
						</>
					))}

					{sectionCard('Identitas Kendaraan', <CarOutlined />, '#1e4d8a', (
						<Row gutter={16}>
							<Col xs={24} sm={8}><Form.Item label="Nomor Polisi" name="nomor_polisi" rules={[{ required: true, message: 'Nomor polisi wajib diisi' }]}><Input style={{ borderRadius: 8 }} className="no-uppercase" /></Form.Item></Col>
							<Col xs={24} sm={8}><Form.Item label="Nomor Polisi Lama" name="nomor_polisi_ex"><Input style={{ borderRadius: 8 }} /></Form.Item></Col>
							<Col xs={24} sm={8}><Form.Item label="No BPKB" name="no_bpkb"><Input style={{ borderRadius: 8 }} /></Form.Item></Col>
							<Col xs={24} sm={12}><Form.Item label="Nomor Rangka" name="nomor_rangka" rules={[{ required: true, message: 'Nomor rangka wajib diisi' }]}><Input style={{ borderRadius: 8 }} /></Form.Item></Col>
							<Col xs={24} sm={12}><Form.Item label="Nomor Mesin" name="nomor_mesin" rules={[{ required: true, message: 'Nomor mesin wajib diisi' }]}><Input style={{ borderRadius: 8 }} /></Form.Item></Col>
						</Row>
					))}

					{sectionCard('Spesifikasi Kendaraan', <SafetyOutlined />, '#389e0d', (
						<Row gutter={16}>
							<Col xs={24} sm={8}>{this._renderSelect('jenis_kendaraan', listJenisKendaraan, 'Jenis Kendaraan', this.handleJenisChange, 'nama', true)}</Col>
							<Col xs={24} sm={8}>{this._renderSelect('merek', listMerek, 'Merek', this.handleMerekChange, 'nama', true)}</Col>
							<Col xs={24} sm={8}>{this._renderSelect('tipe', listTipe, 'Tipe', undefined, 'nama', true)}</Col>
							<Col xs={24} sm={8}><Form.Item label="Tahun Buat" name="tahun_buat" rules={[{ required: true, message: 'Tahun buat wajib diisi' }]}><InputNumber style={{ borderRadius: 8, width: '100%' }} /></Form.Item></Col>
							<Col xs={24} sm={8}><Form.Item label="Warna" name="warna" rules={[{ required: true, message: 'Warna wajib diisi' }]}><Input style={{ borderRadius: 8 }} /></Form.Item></Col>
							<Col xs={24} sm={8}>{this._renderSelect('warna_plat', listWarnaPlat, 'Warna Plat', undefined, 'nama', true)}</Col>
							<Col xs={24} sm={8}>{this._renderSelect('bahan_bakar', listBahanBakar, 'Bahan Bakar')}</Col>
							<Col xs={24} sm={8}><Form.Item label="Silinder (CC)" name="silinder" rules={[{ required: true, message: 'Silinder wajib diisi' }]}><InputNumber style={{ borderRadius: 8, width: '100%' }} /></Form.Item></Col>
							<Col xs={24} sm={8}>{this._renderSelect('fungsi', listFungsi, 'Fungsi', undefined, 'nama', true)}</Col>
							<Col xs={24} sm={8}>{this._renderSelect('golongan', listGolongan, 'Golongan', undefined, 'nama', true)}</Col>
							<Col xs={24} sm={8}>{this._renderSelect('kepemilikan', listKepemilikan, 'Kepemilikan', undefined, 'nama', true)}</Col>
							<Col xs={24} sm={8}><Form.Item label="Kepemilikan Ke" name="kepemilikan_ke" initialValue={1}><InputNumber style={{ borderRadius: 8, width: '100%' }} min={1} /></Form.Item></Col>
						</Row>
					))}

					{sectionCard('Wilayah', <EnvironmentOutlined />, '#722ed1', (
						<Row gutter={16}>
							<Col xs={24} sm={8}>{this._renderSelect('kabupaten', listKabupaten, 'Kabupaten', this.handleKabupatenChange, 'name')}</Col>
							<Col xs={24} sm={8}>{this._renderSelect('kecamatan', listKecamatan, 'Kecamatan', this.handleKecamatanChange, 'name')}</Col>
							<Col xs={24} sm={8}>{this._renderSelect('desa', listDesa, 'Desa', undefined, 'name')}</Col>
						</Row>
					))}

					{sectionCard('Tanggal Pajak (Opsional - isi dari dokumen fisik kalau ada)', <CalendarOutlined />, '#08979c', (
						<Row gutter={16}>
							{DATE_FIELDS.map(({ name, label }) => (
								<Col xs={24} sm={8} key={name}>
									<Form.Item label={label} name={name}>
										<DatePicker format="DD-MM-YYYY" style={{ borderRadius: 8, width: '100%' }} />
									</Form.Item>
								</Col>
							))}
						</Row>
					))}

					<div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginBottom: 30 }}>
						<Link to={configClass.pageUrl.data}><Button style={{ borderRadius: 8, height: 40 }}>Batal</Button></Link>
						<Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={loadingSubmit}
							style={{ background: 'linear-gradient(135deg, #0f2847, #1e4d8a)', border: 'none', borderRadius: 8, fontWeight: 600, height: 40 }}>
							Simpan Kendaraan
						</Button>
					</div>
				</Form>
			</div>
		);
	}
}

const mapStateToProps = (state) => state.configReducers;
export default connect(mapStateToProps, { initHandler, ajaxViewHandler, ajaxHandler, extractResult })(FormTambahKendaraan);
